import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, useRef } from 'react';

import { CallMediaPermissionValue } from '@gem/call/CallStore/Call.types';
import { CallAudioButton } from '@gem/call/CallView/Shared/CallActionsPanel/CallAudioButton/CallAudioButton';
import { CallVideoButton } from '@gem/call/CallView/Shared/CallActionsPanel/CallVideoButton/CallVideoButton';
import { ShareScreenButton } from '@gem/call/components/ShareScreenButton/ShareScreenButton';
import { IcRaiseHand24Icon, IcReportFilled24Icon } from '@gem/icons';
import { IconButton, IconViewSizes, IconView } from '@gem/ui-kit';

import { useCallActionsPanelPresenter } from './CallActionsPanel.presenter';
import styles from './CallActionsPanel.styles.m.css';
import { FinishCallButton } from './FinishCallButton/FinishCallButton';

interface ICallActionsPanelProps {
  className?: string;
  isMinimised?: boolean;
}

export const CallActionsPanel: FC<ICallActionsPanelProps> = observer(
  ({ className, isMinimised }) => {
    const presenter = useCallActionsPanelPresenter();
    const closeAudioPopoverRef = useRef<() => void>(null);
    const closeVideoPopoverRef = useRef<() => void>(null);

    return (
      <div className={classNames(styles.container, className)}>
        {presenter.isShowRiseHand && (
          <IconButton
            Icon={IcRaiseHand24Icon}
            onClick={presenter.toggleHand}
            className={classNames(
              isMinimised ? styles.minimisedButtonContainer : styles.buttonContainer,
              styles.hand
            )}
            size={IconViewSizes.Big}
            iconSize={IconViewSizes.Medium}
            theme={presenter.isHandRaised ? '13' : '12'}
          />
        )}

        <div
          className={isMinimised ? styles.minimisedButtonContainer : styles.buttonContainer}
          onClick={
            presenter.micControlEnabled &&
            presenter.audioPermission === CallMediaPermissionValue.Denied
              ? presenter.showAudioPermissionWarning
              : undefined
          }
        >
          <CallAudioButton
            ref={closeAudioPopoverRef}
            onAudioPopoverOpen={closeVideoPopoverRef.current || undefined}
            audioPermission={presenter.audioPermission}
            checkAndSendPermissionRequest={presenter.checkAndSendPermissionRequest}
            permissions={presenter.permissions}
            micControlEnabled={presenter.micControlEnabled}
          />
          {presenter.micControlEnabled &&
          presenter.audioPermission === CallMediaPermissionValue.Denied ? (
            <IconView
              Icon={IcReportFilled24Icon}
              size={IconViewSizes.Medium}
              className={styles.warningIcon}
            />
          ) : null}
        </div>

        {presenter.isShowControlCamera && (
          <div
            className={isMinimised ? styles.minimisedButtonContainer : styles.buttonContainer}
            onClick={
              presenter.videoPermission === CallMediaPermissionValue.Denied
                ? presenter.showVideoPermissionWarning
                : undefined
            }
          >
            <CallVideoButton
              ref={closeVideoPopoverRef}
              onVideoPopoverOpen={closeAudioPopoverRef.current || undefined}
              videoPermission={presenter.videoPermission}
              checkAndSendPermissionRequest={presenter.checkAndSendPermissionRequest}
              permissions={presenter.permissions}
            />
            {presenter.videoPermission === CallMediaPermissionValue.Denied ? (
              <IconView
                Icon={IcReportFilled24Icon}
                size={IconViewSizes.Medium}
                className={styles.warningIcon}
              />
            ) : null}
          </div>
        )}

        {presenter.isShowControlScreenSharing && (
          <ShareScreenButton
            className={isMinimised ? styles.minimisedButtonContainer : styles.buttonContainer}
            isDisabled={presenter.ongoingCall.isDisabledShareScreen || !presenter.isCallStarted}
            isActive={presenter.isSharedScreen}
            shareScreen={presenter.shareScreen}
            stopSharingScreen={presenter.stopSharingScreen}
          />
        )}

        <FinishCallButton />
      </div>
    );
  }
);
